import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  model,
  output,
  OutputEmitterRef,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheet, MatBottomSheetModule, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatChip, MatChipListbox, MatChipRemove } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatInput, MatPrefix } from '@angular/material/input';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { IrdButtonComponent, IrdIconComponent, IrdIconRegistryService } from '@ird/ng-headless';
import { spxIconArrowDown01Sharp, spxIconFilterOutlined } from '@ird/shared-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { ClearableInputDirective, IconComponent, SpinnerComponent } from '../base';
import { FilterAccordionComponent } from './components';
import { BottomSheetFilterAccordionWrapperComponent } from './components/bottom-sheet-filter-accordion-wrapper/bottom-sheet-filter-accordion-wrapper.component';
import { FilterService } from './filter.service';
import * as deTranslations from './i18n/de-lang.json';
import * as enTranslations from './i18n/en-lang.json';
import { FilterOption, Filters, MainFilterOption } from './models';
import { BaseFilterComponent } from './directives';

interface SelectedFilterChip {
  key: string;
  optionLabel?: string;
  value: string;
  filterLabel: string;
}

@Component({
  selector: 'spx-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    FormsModule,
    MatChip,
    MatChipListbox,
    MatListOption,
    MatMenuTrigger,
    MatSelectionList,
    MatSidenav,
    TranslateModule,
    MatDivider,
    MatChipRemove,
    MatSidenavContainer,
    IconComponent,
    IrdButtonComponent,
    IrdIconComponent,
    FilterAccordionComponent,
    MatBottomSheetModule,
    MatInput,
    MatFormField,
    ReactiveFormsModule,
    MatMenu,
    MatPrefix,
    ClearableInputDirective,
    SpinnerComponent,
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
  ],
  providers: [FilterService],
})
export class FilterComponent extends BaseFilterComponent implements AfterViewInit {
  public filterOptions = input.required<MainFilterOption[]>();
  public filters = model<Filters>({});
  public showOnlyValues = input(false);
  public showAllFilters = input(false);
  public showFilterChips = input(true);
  public showMobileFilterButton = input(true);
  public showMobileFilterChips = input(false);
  public results = input<number | undefined>(undefined);

  public filterOpen: OutputEmitterRef<string> = output();
  public filterSent: OutputEmitterRef<FilterOption> = output();
  public sheetDismissed: OutputEmitterRef<void> = output();

  public menus = viewChildren(MatMenu);
  public sidenav = viewChild(MatSidenav);
  public filterLabels = signal<Record<string, string>>({});
  public menuRefs = signal<Record<string, MatMenu | null>>({});
  public openedMenu = signal<string | undefined>(undefined);
  public isSidenavSwitchingState = signal(false);
  public showClearAll = computed(() => Object.values(this.filters()).filter((v) => !!v).length > 0);
  public selectedFilterChips = computed<SelectedFilterChip[]>(() =>
    Object.entries(this.filters()).reduce((chips: SelectedFilterChip[], entry) => {
      const selectedFilter = this.localFilterOptions().find((o) => o.key === entry[0])!;
      const [filterKey, filterOptions] = entry;
      filterOptions?.forEach((filter) => {
        const label = selectedFilter.options ? selectedFilter.options.find((o) => o.key === filter)?.label : selectedFilter.label;
        const optionLabel = label ?? filter;
        chips.push({
          optionLabel: selectedFilter.label === optionLabel ? undefined : optionLabel,
          value: filter,
          key: filterKey,
          filterLabel: selectedFilter.label,
        });
      });
      return chips;
    }, []),
  );
  protected filterLength = computed(() =>
    Object.values(this.filters()).reduce((sum, entry) => {
      const value = Array.isArray(entry) ? entry.length : 1;
      return sum + value;
    }, 0),
  );
  private readonly breakPointObserver = inject(BreakpointObserver);
  protected isMobile = toSignal(this.breakPointObserver.observe([Breakpoints.XSmall]).pipe(map((r) => r.matches)));
  private readonly translate = inject(TranslateService);
  private readonly _bottomSheet = inject(MatBottomSheet);
  private _bottomSheetRef: MatBottomSheetRef | null = null;

  constructor() {
    super();
    // ng-base ships to apps that never call provideIrdIcons, so the icons this template needs
    // are registered here rather than assumed to be in the app-wide set.
    inject(IrdIconRegistryService).registerAll([spxIconArrowDown01Sharp, spxIconFilterOutlined]);

    this.translate.setTranslation('en', enTranslations, true);
    this.translate.setTranslation('de', deTranslations, true);

    effect(() => {
      this.filterService.initialize(this.filterOptions());
    });

    effect(() => {
      const options = this.localFilterOptions();
      const menus = this.menus();

      if (options.length === menus?.length) {
        setTimeout(() => {
          this.menuRefs.update((refs) => {
            options
              .filter((f) => f.options)
              .forEach((option, index) => {
                refs[option.key] = menus?.[index] || null;
              });
            return refs;
          });
        });
      }
    });

    effect(() => {
      const mobile = this.isMobile();
      if (mobile === undefined) {
        return;
      }

      if (mobile) {
        if (this.sidenav()?.opened) {
          this.sidenav()?.close();
          this.isSidenavSwitchingState.set(false);
        }
      } else {
        if (this._bottomSheetRef) {
          this._bottomSheetRef.dismiss();
        }
      }
    });
  }

  @HostListener('window:click', ['$event'])
  public onClick(event: MouseEvent): void {
    if (
      this.sidenav()?.opened &&
      !this.isSidenavSwitchingState() &&
      !this.sidenav()?._content.nativeElement.contains(event.target as Node)
    ) {
      this.toggleSidenav();
    }
  }

  ngAfterViewInit() {
    this.sidenav()?._closedStream.subscribe(() => this.isSidenavSwitchingState.set(false));
    this.sidenav()?._openedStream.subscribe(() => this.isSidenavSwitchingState.set(false));
  }

  public onMenuOpen(option: MainFilterOption): void {
    this.openedMenu.set(option.key);
    this.filterOpen.emit(option.key);

    this.loadInitialData(option);
  }

  public toggleFilter(key: string, value: string | boolean) {
    const newFilters = this.filterService.toggleFilter(this.filters(), key, value);
    if (newFilters) {
      const { options } = this.localFilterOptions().find((o) => o.key === key)!;
      if (options) {
        this.adjustExpansionPanelDescriptions(key);
      }

      this.filters.set({ ...newFilters });
      if (newFilters[key]?.includes?.(value.toString())) {
        this.filterSent.emit({ key, label: value.toString() });
      }
    }
  }

  public clearFilters() {
    this.filters.set({});
    this.filterLabels.set({});
  }

  public toggleSidenav(): void {
    if (this.sidenav()?.opened) {
      this.sidenav()?.close();
    } else {
      this.sidenav()?.open();
    }
    this.isSidenavSwitchingState.set(true);
  }

  public adjustExpansionPanelDescriptions(key: string): void {
    const selected = this.filters()[key] || [];

    this.filterLabels.update((labels) => {
      const options = this.localFilterOptions().find((o) => o.key === key)?.options;
      labels[key] = selected.map((s) => options?.find((o) => o.key === s)?.label).join(', ');
      return labels;
    });
  }

  protected isSelected(mainOptionKey: string, optionKey: string): boolean {
    return (
      this.filters()[mainOptionKey]?.some((k) => {
        return k === optionKey;
      }) || false
    );
  }

  public openFilterBottomSheet(): void {
    if (this.isMobile() !== true || this._bottomSheetRef) {
      return;
    }

    this._bottomSheetRef = this._bottomSheet.open(BottomSheetFilterAccordionWrapperComponent, {
      disableClose: true,
      panelClass: 'mat-bottom-sheet-panel',
      data: { filters: this.filters(), filterOptions: this.localFilterOptions() },
    });

    this._bottomSheetRef.afterDismissed().subscribe((result) => {
      this._bottomSheetRef = null;
      if (result) {
        this.filters.set(result);
      }
      this.sheetDismissed.emit();
    });
  }
}
